import { ListaSpedizione } from '../ListaSpedizione';
import { VoceGiacenza } from '../VoceGiacenza';

// ── Natura del dato di una colonna (feature 119, issue #3359) ────────────────────────────────
//
// Descrive COME SI LEGGE un dato, non come si scrive: la formattazione resta nel template,
// con le pipe Angular (research D10). Guida la resa data-driven della card mobile.
//
// `badge` e' di SOLA TESTATA della card (invariante I-5, research D18): le uniche due colonne
// che lo dichiarano (`Stato`, `NumRegistraz`) sono rese nell'intestazione della card e non
// passano mai dal blocco pill, che non ha un ramo per loro. Una colonna `badge` fra le pill
// cadrebbe nel ramo di default e sarebbe resa come testo semplice, senza alcun errore: il
// vincolo e' asserito dal test di copertura del catalogo, non lasciato all'attenzione.
//
// `count` e' la natura degli IDENTIFICATIVI E CONTATORI, distinta da `number` (quantita'):
// va resa SENZA separatore delle migliaia. Il progetto registra `LOCALE_ID: 'it-IT'`
// (`app.module.ts:38`), quindi `| number` su un identificativo a sette cifre produrrebbe
// `1.234.567` — che non e' cio' che la tabella mostra (quelle colonne sono interpolate
// grezze) e non e' cercabile (FR-017b, research D19).
//
// Nessun valore `link`: nessuna colonna e' un punto di navigazione, ne' in tabella ne' in
// card — l'apertura dell'ordine di spedizione e' sul pulsante dedicato (research D9).
export type CellKind = 'text' | 'mono' | 'number' | 'count' | 'date' | 'flag' | 'badge';

// Proprieta' scalare di `ListaSpedizione` da cui una colonna legge il proprio valore.
// TIPIZZATO, non `string` (FR-017c, research D21): il progetto non imposta
// `compilerOptions.strict` in tsconfig.json, quindi senza `noImplicitAny` un refuso come
// `field: 'qtaOrdUmDco'` sarebbe un `any` implicito su `row[field]` e passerebbe la
// compilazione, protetto solo dal test a valle. Con il tipo stretto lo intercetta
// l'annotazione gia' presente su LISTA_SPEDIZIONI_COLUMNS.
// `giacenze` e' ESCLUSO, non incluso per comodita': e' l'unica proprieta' non scalare
// (`Record<string, number | null>`) e includerla allargherebbe il tipo di ritorno
// dell'helper di cella oltre cio' che le pipe di formattazione accettano. Le giacenze non
// passano mai da `field` — invariante I-2 — quindi l'esclusione non toglie nulla.
export type ListaSpedizioneField = Exclude<keyof ListaSpedizione, 'giacenze'>;

// Catalogo colonne della tabella Excel-like della Lista Spedizioni (feature 048).
// Le `key` DEVONO coincidere con i nomi PascalCase delle proprietà backend usate dai
// selettori di raggruppamento (ListaSpedizioniGroupedEsolver / groupColumns).
// `groupable` filtra le colonne ammesse nel modale "Raggruppa per".
//
// `code`, `field` e `kind` sono OPZIONALI (feature 119): il catalogo e' consumato anche da
// `lista-spedizioni-grid.helper.ts` e dal modale di raggruppamento, e campi obbligatori
// avrebbero imposto modifiche a quei consumatori senza alcun guadagno.
// Invarianti:
//   I-1: `code` valorizzato ⟺ isGiacenzaColumnKey(key)   — identificazione MAGAZZINO.AREA
//   I-2: `field` valorizzato ⟺ !isGiacenzaColumnKey(key) — le giacenze leggono dal dizionario
//   I-3: `kind` valorizzato per OGNI voce (statiche e giacenze)
export interface ListaSpedizioneColumn {
  key: string;
  label: string;
  groupable: boolean;
  // Identificativo `MAGAZZINO.AREA` (solo `MAGAZZINO` per una voce senza aree). Solo giacenze.
  code?: string;
  // Proprietà camelCase di ListaSpedizione da cui leggere il valore. Solo colonne statiche.
  field?: ListaSpedizioneField;
  // Natura del dato: guida la resa della card mobile (FR-017).
  kind?: CellKind;
}

// `field` e `kind` (feature 119): il catalogo e' la fonte di verita' anche per il rendering
// della card mobile. Le sei colonne `count` — NumProgrRiga, DatiSpedNum, PrioritaOrdine,
// TipoAnagrafica, IdDocOrd, IdDoc — sono esattamente quelle che la tabella interpola SENZA
// pipe (`spedizione-lista-row.component.html:107, 120, 140, 214, 220, 226`): dichiararle
// `number` metterebbe il punto delle migliaia su un identificativo (invariante I-6).
export const LISTA_SPEDIZIONI_COLUMNS: ListaSpedizioneColumn[] = [
  // anchor, sempre visibile. `mono` e' resa in monospazio evidenziato, NON un punto di
  // navigazione: l'apertura dell'ordine resta sul pulsante dedicato (FR-015).
  { key: 'CodArt',             label: 'Codice Articolo',                          groupable: false, field: 'codArt',             kind: 'mono'   },
  { key: 'DesArt',             label: 'Descrizione Articolo',                     groupable: false, field: 'desArt',             kind: 'text'   },
  { key: 'Cliente',            label: 'Cliente',                                  groupable: true,  field: 'cliente',            kind: 'text'   },
  { key: 'Stato',              label: 'Stato',                                    groupable: true,  field: 'stato',              kind: 'badge'  },
  { key: 'DataSpedizione',     label: 'Data Spedizione',                          groupable: true,  field: 'dataSpedizione',     kind: 'date'   },
  { key: 'DataConsegnaRich',   label: 'Data Consegna Richiesta',                  groupable: true,  field: 'dataConsegnaRich',   kind: 'date'   },
  { key: 'NumDocOriginale',    label: 'Numero documento OdV',                     groupable: true,  field: 'numDocOriginale',    kind: 'mono'   },
  { key: 'UnitaMisura',        label: 'UdM',                                      groupable: true,  field: 'unitaMisura',        kind: 'text'   },
  { key: 'QtaOrdUmDoc',        label: 'Quantita Ordinato',                        groupable: false, field: 'qtaOrdUmDoc',        kind: 'number' },
  { key: 'QtOrdImpUmDoc',      label: 'Quantita Residua',                         groupable: false, field: 'qtOrdImpUmDoc',      kind: 'number' },
  { key: 'NumRegistraz',       label: 'Numero Registrazione OdS',                 groupable: false, field: 'numRegistraz',       kind: 'badge'  },
  { key: 'NumProgrRiga',       label: 'Numero Progressivo Riga',                  groupable: false, field: 'numProgrRiga',       kind: 'count'  },
  { key: 'CodArtCliFor',       label: 'Codice Articolo Presso Clienti/Fornitori', groupable: false, field: 'codArtCliFor',       kind: 'mono'   },
  // Campi aggiuntivi della vista ZVIEWLISTASPEDIZIONI, off di default (attivabili dal selettore).
  { key: 'DatiSpedNum',        label: 'Codice indirizzo',                         groupable: false, field: 'datiSpedNum',        kind: 'count'  },
  { key: 'DatiSpedLocalita1',  label: 'Comune',                                   groupable: true,  field: 'datiSpedLocalita1',  kind: 'text'   },
  { key: 'ValoreCampo2',       label: 'Des (campo utente)',                       groupable: true,  field: 'valoreCampo2',       kind: 'text'   },
  { key: 'PrioritaOrdine',     label: 'Priorita ordine',                          groupable: false, field: 'prioritaOrdine',     kind: 'count'  },
  { key: 'DataConsegnaConf',   label: 'Data Consegna Confermata',                 groupable: true,  field: 'dataConsegnaConf',   kind: 'date'   },
  // Sorgente numerica, semantica booleana: reso `Sì` / `-`.
  { key: 'RigaSollecitata',    label: 'Riga Sollecitata (0-No;1-Si)',             groupable: false, field: 'rigaSollecitata',    kind: 'flag'   },
  { key: 'SerieNumApp',        label: 'Riferimento Registrazione',                groupable: true,  field: 'serieNumApp',        kind: 'mono'   },
  { key: 'QtaConf',            label: 'Quantita Pronta da Spedire',               groupable: false, field: 'qtaConf',            kind: 'number' },
  { key: 'PrelevatoImb',       label: 'Prelevato IMB',                            groupable: false, field: 'prelevatoImb',       kind: 'number' },
  { key: 'NotaInternaPers',    label: 'Nota Interna Vendite',                     groupable: false, field: 'notaInternaPers',    kind: 'text'   },
  { key: 'NotaInternaOsSpedp', label: 'Nota Interna Logistica',                   groupable: false, field: 'notaInternaOsSpedp', kind: 'text'   },
  // Colonne della nuova interrogazione (feature 117). Tutte non raggruppabili: identificativi
  // e quantita sono quasi-univoci e darebbero un gruppo per riga. Il raggruppamento "per
  // cliente" resta sulla chiave `Cliente`, che ora mostra la ragione sociale come etichetta.
  // `StatoImg` (stato numerico) e' esposto dall'API ma resta fuori dal catalogo: il suo
  // consumo previsto e' grafico, non tabellare.
  { key: 'RagSoc',             label: 'Ragione sociale',                          groupable: false, field: 'ragSoc',             kind: 'text'   },
  { key: 'VarianteArt',        label: 'Variante Articolo',                        groupable: false, field: 'varianteArt',        kind: 'mono'   },
  { key: 'TipoAnagrafica',     label: 'Tipo Anagrafica Articolo',                 groupable: false, field: 'tipoAnagrafica',     kind: 'count'  },
  // Feature 157: off di default come gli altri campi aggiuntivi; filtrabile dalla barra dei filtri.
  { key: 'CodFamiglia',        label: 'Famiglia',                                 groupable: false, field: 'codFamiglia',        kind: 'mono'   },
  { key: 'IdDocOrd',           label: 'Id Ordine Cliente',                        groupable: false, field: 'idDocOrd',           kind: 'count'  },
  { key: 'IdDoc',              label: 'Id documento',                             groupable: false, field: 'idDoc',              kind: 'count'  },
  // Sentinella 1800-01-01 ⇒ valore assente: la regola vive nell'helper di cella, non qui.
  { key: 'DataDocumento',      label: 'Data Approntamento',                       groupable: false, field: 'dataDocumento',      kind: 'date'   },
  { key: 'QtInSped',           label: 'Quantità in spedizione ORV',               groupable: false, field: 'qtInSped',           kind: 'number' },
  { key: 'QtCons',             label: 'Quantità consegnata ORV',                  groupable: false, field: 'qtCons',             kind: 'number' },
  { key: 'QtaResOrv',          label: 'Quantità residua ORV',                     groupable: false, field: 'qtaResOrv',          kind: 'number' },
];

// ── Colonne giacenza dinamiche (feature 118, issue #3354) ───────────────────────────────────
//
// Il prefisso `Giac:` rende la chiave riconoscibile con startsWith e non collide con nessuna
// chiave statica; `|` e `:` non sono ammessi nei codici ESOLVER, e nessuno dei due e' una virgola
// (le preferenze colonne sono persistite in un CSV).
export const GIACENZA_COLUMN_PREFIX = 'Giac:';

// Chiave della colonna per una voce di giacenza. Il separatore `|` c'e' SEMPRE, anche quando la
// parte destra e' vuota: e' cio' che distingue una voce di magazzino ben formata (`Giac:DEP|`) da
// un token malformato, che la TVF non saprebbe interpretare.
export function giacenzaColumnKey(voce: VoceGiacenza): string {
  return `${GIACENZA_COLUMN_PREFIX}${voce.codMag}|${voce.codAreaMag}`;
}

// Chiave del dizionario `giacenze` del DTO, ricavata dalla chiave di colonna.
export function giacenzaDictKey(columnKey: string): string {
  return columnKey.slice(GIACENZA_COLUMN_PREFIX.length);
}

export function isGiacenzaColumnKey(key: string): boolean {
  return (key ?? '').startsWith(GIACENZA_COLUMN_PREFIX);
}

// Codice identificativo della voce, nella stessa convenzione di ZVIEWGIACENZA: "GLT" per una
// voce di magazzino, "GLT.M01" per una voce di area (118/FR-011a).
//
// Feature 119: non e' piu' solo un'etichetta di ripiego. E' (a) il metadato `code` di ogni
// colonna di giacenza — valorizzato SEMPRE, perche' e' cio' che disambigua due aree con la
// stessa descrizione su magazzini diversi (FR-002) — e (b) il termine di confronto con cui si
// riconosce una descrizione SURROGATA dal backend (FR-003, research D2). Resta la sola
// implementazione lato client della convenzione `MAG.AREA`.
function etichettaMagArea(voce: VoceGiacenza): string {
  return voce.codAreaMag ? `${voce.codMag}.${voce.codAreaMag}` : voce.codMag;
}

// Etichetta di ripiego quando la descrizione anagrafica non aggiunge nulla al codice (FR-003).
// Il codice nudo non compare mai come etichetta: l'utente lo legge come un errore
// dell'applicazione, non come il nome di un'area.
export function giacenzaFallbackLabel(code: string): string {
  return `Giacenza ${code}`;
}

// Vero quando il `code` va mostrato ACCANTO all'etichetta — nel selettore, nell'intestazione
// della tabella e nella pill della card (FR-005, FR-008, FR-018). Falso quando l'etichetta e'
// gia' quella di ripiego, che il codice lo contiene: ripeterlo direbbe due volte la stessa
// cosa nello stesso riquadro. Una regola sola per tre consumatori (data-model §4).
export function mostraCodiceGiacenza(column: ListaSpedizioneColumn | undefined): boolean {
  return !!column?.code && column.label !== giacenzaFallbackLabel(column.code);
}

// Riceve le voci GIA' filtrate e ordinate dal backend (GetVociGiacenza): una voce e' un'area,
// oppure un magazzino senza gestione per area — nel qual caso codAreaMag e' "" (FR-011a).
// Filtro e ordinamento NON si ripetono qui: sono la stessa sorgente da cui il backend costruisce
// i token della query, e duplicare la regola sui due lati significherebbe poterla disallineare.
//
// Feature 119: il confronto per il ripiego dell'etichetta e' con il CODICE, non con il vuoto.
// `descrizione` non arriva mai vuota — `MagazziniService.GetVociGiacenza` la surroga gia' con
// il codice (`MagazziniService.cs:295` per i magazzini, `:304` per le aree) — quindi un
// `descrizione || …` non scatterebbe MAI e l'etichetta resterebbe il codice nudo `GLT.M01`,
// cioe' il difetto che la issue segnala, con una build verde. Il confronto e' esatto perche'
// la stringa surrogata coincide carattere per carattere con `etichettaMagArea`.
// Il backend NON si tocca (FR-024): la correzione vive tutta qui (research D2).
export function buildGiacenzaColumns(voci: VoceGiacenza[]): ListaSpedizioneColumn[] {
  return (voci ?? []).map(v => {
    const code = etichettaMagArea(v);
    return {
      key: giacenzaColumnKey(v),
      label: (v.descrizione && v.descrizione !== code) ? v.descrizione : giacenzaFallbackLabel(code),
      groupable: false, // FR-024: mai criterio di raggruppamento
      code,             // sempre, anche con descrizione valida: e' cio' che disambigua (FR-002)
      kind: 'number' as const, // quantita': in tabella e' resa con `| number:'1.0-3'`
    };
  });
}
